import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import {
  resolveSiteFlowConditionTypeLabel,
  resolveSiteFlowTagLabels,
} from '../../domain/condition_families/siteFlowConditionFamily';
import {
  buildContributingConditionDisplayLine,
  hasWorkplaceConditionContent,
  readWorkplaceCondition,
  resolveContributingConditionStatusLabel,
  resolveInputNeededFromLabel,
  type WorkplaceConditionFields,
} from '../../domain/workplaceCondition/workplaceCondition';
import { WORKER_SUPPORT_NOTE_TEXT } from '../../domain/reportLanguage/reportLanguageGuardrails';
import {
  hasWorkplaceConditionContextContent,
  type WorkplaceConditionContext,
} from '../../domain/workplaceCondition/workplaceConditionContext';
import WorkplaceConditionContextRows from './WorkplaceConditionContextRows';

type WorkplaceConditionObservedSectionProps = {
  scopeOrPayload?: Record<string, unknown> | null;
  workplaceCondition?: WorkplaceConditionFields | null;
  conditionContext?: WorkplaceConditionContext | null;
};

export function resolveWorkplaceConditionFromArtifact(
  persistedReport?: {
    report_payload?: Record<string, unknown> | null;
    report_content?: Record<string, unknown> | null;
    finalized_payload?: Record<string, unknown> | null;
  } | null,
): WorkplaceConditionFields {
  const content = persistedReport?.report_content
    || persistedReport?.finalized_payload
    || persistedReport?.report_payload;
  if (!content || typeof content !== 'object') return {};
  const direct = (content as Record<string, unknown>).workplace_condition;
  if (!direct || typeof direct !== 'object') return {};
  return { ...(direct as WorkplaceConditionFields) };
}

export const WorkplaceConditionObservedSection: React.FC<WorkplaceConditionObservedSectionProps> = ({
  scopeOrPayload,
  workplaceCondition,
  conditionContext,
}) => {
  const fields = workplaceCondition && hasWorkplaceConditionContent(workplaceCondition)
    ? workplaceCondition
    : readWorkplaceCondition(scopeOrPayload);
  const showContext = hasWorkplaceConditionContextContent(conditionContext);
  if (!hasWorkplaceConditionContent(fields) && !showContext) return null;

  const conditionTypeLabel = resolveSiteFlowConditionTypeLabel(fields.condition_type);
  const tagLabels = resolveSiteFlowTagLabels(fields.condition_tags);
  const contributingLine = buildContributingConditionDisplayLine(
    fields.likely_contributing_condition,
    fields.contributing_condition_status,
  );
  const statusLabel = resolveContributingConditionStatusLabel(fields.contributing_condition_status);
  const inputNeededLabel = resolveInputNeededFromLabel(fields.input_needed_from);

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, mb: 3 }}
      data-testid="workplace-condition-observed-section"
    >
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Workplace Condition Observed
        </Typography>
        <Stack spacing={1.25}>
          {conditionTypeLabel ? (
            <Typography variant="body2" color="text.secondary">
              Condition type: {conditionTypeLabel}
            </Typography>
          ) : null}
          {tagLabels.length ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {tagLabels.map((label) => (
                <Chip key={label} size="small" variant="outlined" label={label} />
              ))}
            </Stack>
          ) : null}
          {fields.condition_summary ? (
            <Typography variant="body1">{fields.condition_summary}</Typography>
          ) : null}
          {fields.why_this_matters_text ? (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Why this matters
              </Typography>
              <Typography variant="body2">{fields.why_this_matters_text}</Typography>
            </Box>
          ) : null}
          {contributingLine ? (
            <Typography variant="body2" color="text.secondary" data-testid="contributing-condition-display-line">
              {contributingLine}
            </Typography>
          ) : null}
          {statusLabel && !contributingLine ? (
            <Typography variant="body2" color="text.secondary">
              Contributing condition status: {statusLabel}
            </Typography>
          ) : null}
          {inputNeededLabel ? (
            <Typography variant="body2" color="text.secondary">
              Input needed from: {inputNeededLabel}
            </Typography>
          ) : null}
          {fields.reviewer_notes ? (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Reviewer notes
              </Typography>
              <Typography variant="body2">{fields.reviewer_notes}</Typography>
            </Box>
          ) : null}
          {fields.worker_support_note_enabled !== false ? (
            <Typography variant="body2" color="text.secondary">
              {WORKER_SUPPORT_NOTE_TEXT}
            </Typography>
          ) : null}
          {showContext && conditionContext ? (
            <WorkplaceConditionContextRows context={conditionContext} />
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default WorkplaceConditionObservedSection;
